/**
 * Class to manage websocket connection
 */
export class WebSocketConnection {
    /**
     * Constructor
     * @param socket Socket Object
     * @param logger Logger - ILogger
     */
    constructor(socket, logger) {
        this.bufferOnMessage = [];
        this.isReconnecting = false;
        /**
         * Register visibility and interaction listeners to reconnect the socket
         * when the page regains focus (handles all browsers, not just Safari).
         */
        this.registerVisibilityReconnect = () => {
            const interactionActions = ["touchstart", "mouseover"];
            // --- Interaction-based fallback (touchstart / mouseover) ---
            const fnInteractionReconnect = async (e) => {
                var _a, _b;
                if (((_a = this._socket) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN ||
                    ((_b = this._socket) === null || _b === void 0 ? void 0 : _b.readyState) === WebSocket.CONNECTING)
                    return;
                if (this.isReconnecting)
                    return;
                interactionActions.forEach((action) => {
                    document.removeEventListener(action, fnInteractionReconnect);
                });
                this.isReconnecting = true;
                const reconnected = await this.reconnectWithBackoff();
                this.isReconnecting = false;
                if (!reconnected) {
                    this._logger.log("WebSocketConnection: interaction-based reconnect failed.");
                }
                interactionActions.forEach((action) => {
                    document.addEventListener(action, fnInteractionReconnect, {
                        once: true,
                    });
                });
            };
            interactionActions.forEach((action) => {
                document.addEventListener(action, fnInteractionReconnect, {
                    once: true,
                });
            });
            // --- Permanent visibilitychange listener (T3) ---
            document.addEventListener("visibilitychange", async () => {
                var _a, _b;
                if (document.visibilityState !== "visible")
                    return;
                if (((_a = this._socket) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN ||
                    ((_b = this._socket) === null || _b === void 0 ? void 0 : _b.readyState) === WebSocket.CONNECTING)
                    return;
                if (this.isReconnecting)
                    return;
                this._logger.log("WebSocketConnection: page became visible, socket not open — reconnecting.");
                this.isReconnecting = true;
                const reconnected = await this.reconnectWithBackoff();
                this.isReconnecting = false;
                if (!reconnected) {
                    this._logger.log("WebSocketConnection: visibility-based reconnect failed.");
                }
            });
        };
        this.applyEvents = () => {
            if (!this._socket)
                return;
            this._socket.onopen = () => this.onOpen();
            this._socket.onclose = () => this.onClose();
            this._socket.onerror = (event) => {
                const errorMsg = event instanceof ErrorEvent ? event.message : "Unknown error";
                this.onError(errorMsg);
            };
        };
        this.dispatchMessage = (event) => {
            const suspensionDetected = this.isSuspensionMessage(event);
            this.bufferOnMessage.forEach((handler) => handler(event));
            if (suspensionDetected) {
                void this.handleSuspension();
            }
        };
        /**
         * Register message event handler
         * @param handler Handler to be called when message is received
         */
        this.onMessage = (handler) => {
            if (!this._socket)
                return;
            this.bufferOnMessage.push(handler);
            this._logger.log(`WebSocketConnection.onMessage`);
            this._socket.onmessage = this.dispatchMessage;
        };
        /**
         * Remove a specific message handler from the buffer.
         * @param handler The handler function to remove
         */
        this.removeMessageHandler = (handler) => {
            this.bufferOnMessage = this.bufferOnMessage.filter((h) => h !== handler);
            this._logger.log("WebSocketConnection.removeMessageHandler");
        };
        /**
         * Open socket Event
         */
        this.onOpen = () => {
            this._logger.log("WebSocketConnection.onOpen");
            window.postMessage("WebSocketConnection.onOpen", "*");
        };
        /**
         * Close socket Event
         */
        this.onClose = () => {
            this._logger.log("WebSocketConnection.onClose");
        };
        /**
         * Error socket Event
         * @param error Error message
         */
        this.onError = (error) => {
            this._logger.log(`WebSocketConnection.onError: ${error}`);
        };
        /**
         * Send Message to socket
         * @param message Message to send
         */
        this.send = (message) => {
            var _a, _b;
            if (!this._socket)
                return;
            const messageString = JSON.stringify(message);
            const { data = "" } = message;
            let isActiveLocalCurrency = false;
            try {
                const parsed = JSON.parse(data);
                isActiveLocalCurrency = (_a = parsed === null || parsed === void 0 ? void 0 : parsed.isActiveLocalCurrency) !== null && _a !== void 0 ? _a : false;
            }
            catch (_c) {
                this._logger.log("WebSocketConnection.send: Invalid JSON data");
            }
            (_b = message.reintent) !== null && _b !== void 0 ? _b : (message.reintent = 0);
            if (this._socket.readyState !== WebSocket.OPEN) {
                if (message.reintent > 10 && !isActiveLocalCurrency) {
                    // Instead of reloading the page, trigger a reconnect and re-enqueue the message
                    this._logger.log("WebSocketConnection: send() exceeded 10 retries — triggering reconnect instead of reload.");
                    if (!this.isReconnecting) {
                        this.isReconnecting = true;
                        void this.reconnectWithBackoff().then((reconnected) => {
                            this.isReconnecting = false;
                            if (reconnected) {
                                message.reintent = 0;
                                this.send(message);
                            }
                            else {
                                this._logger.log("WebSocketConnection: reconnect from send() failed — message dropped.");
                            }
                        });
                    }
                    else {
                        // Already reconnecting — cap delay and keep retrying
                        const cappedDelay = Math.min(100 * message.reintent, 3000);
                        setTimeout(() => this.send(message), cappedDelay);
                    }
                    return;
                }
                this._logger.log(`WebSocketConnection IS NOT OPEN (${this._socket.readyState}): ${messageString}`);
                message.reintent++;
                setTimeout(() => this.send(message), 100 * message.reintent);
                return;
            }
            this._socket.send(messageString);
            this._logger.log(`WebSocketConnection.send: ${messageString}`);
        };
        /**
         * Close Socket connection
         */
        this.close = () => {
            if (!this._socket)
                return;
            this._socket.close();
            this._logger.log("WebSocketConnection.close");
        };
        this.restoreMessageHandlers = () => {
            const handlers = [...this.bufferOnMessage];
            this.bufferOnMessage = [];
            handlers.forEach((handler) => this.onMessage(handler));
        };
        this.handleSuspension = async () => {
            if (this.isReconnecting)
                return;
            this.isReconnecting = true;
            this._logger.log("WebSocketConnection suspension detected. Restarting connection.");
            window.postMessage("WebSocketConnection.onSuspension", "*");
            if (this._socket) {
                try {
                    this._socket.close();
                }
                catch (_a) {
                    // Ignore close errors during suspension handling
                }
                this._socket = null;
            }
            const reconnected = await this.reconnectWithBackoff();
            if (!reconnected) {
                this._logger.log("WebSocketConnection failed to reconnect after suspension.");
            }
            this.isReconnecting = false;
        };
        this.urlSocket = socket.url;
        this._socket = socket;
        this._logger = logger;
        this.applyEvents();
        this.registerVisibilityReconnect();
        // reconnect socket every 60 seconds
        setInterval(() => {
            var _a;
            const url = new URL(this.urlSocket);
            (_a = this._socket) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify({ event: "connecting", id: url.searchParams.get("id") }));
        }, 60 * 1000);
    }
    async reconnectOnce() {
        try {
            const socketReconnect = new WebSocket(this.urlSocket);
            return await new Promise((resolve) => {
                socketReconnect.onopen = () => {
                    this._socket = socketReconnect;
                    this.applyEvents();
                    this.restoreMessageHandlers();
                    resolve(true);
                };
                socketReconnect.onerror = () => resolve(false);
            });
        }
        catch (_a) {
            return false;
        }
    }
    async reconnectWithBackoff(maxAttempts = 5) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const success = await this.reconnectOnce();
            if (success) {
                return true;
            }
            this._logger.log(`WebSocketConnection reconnect attempt ${attempt} failed after suspension.`);
            if (attempt < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
            }
        }
        return false;
    }
    hasSuspensionFlag(value, keyName) {
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (parsed && typeof parsed === "object" && this.hasSuspensionFlag(parsed)) {
                    return true;
                }
            }
            catch (_a) {
                // Non-JSON strings are expected; fall through to text checks.
            }
            const normalized = value.toLowerCase();
            if (normalized.includes("suspension") ||
                normalized.includes("suspended") ||
                normalized.includes("websocket is closed due to suspension")) {
                return true;
            }
            if (normalized === "hide" ||
                normalized === "hidden" ||
                normalized.includes('"hide":true') ||
                normalized.includes('"hide": true') ||
                normalized.includes('"shouldhide":true') ||
                normalized.includes('"shouldhide": true') ||
                normalized.includes('"action":"hide"') ||
                normalized.includes('"action": "hide"')) {
                return true;
            }
            if (keyName) {
                const normalizedKey = keyName.toLowerCase();
                if (normalizedKey.includes("hide") &&
                    (normalized === "true" || normalized === "1")) {
                    return true;
                }
            }
            return false;
        }
        if (typeof value === "boolean") {
            if (!value || !keyName)
                return false;
            const normalizedKey = keyName.toLowerCase();
            return normalizedKey.includes("hide") || normalizedKey.includes("suspend");
        }
        if (value && typeof value === "object") {
            if (Array.isArray(value)) {
                return value.some((item) => this.hasSuspensionFlag(item));
            }
            return Object.entries(value).some(([nestedKey, nestedValue]) => this.hasSuspensionFlag(nestedValue, nestedKey));
        }
        return false;
    }
    isSuspensionMessage(event) {
        var _a;
        const data = event.data;
        if (typeof data === "string") {
            const normalized = data.toLowerCase();
            if (normalized.includes("suspension") || normalized.includes("suspended")) {
                return true;
            }
            try {
                const parsed = JSON.parse(data);
                if (parsed && typeof parsed === "object") {
                    const eventName = String((_a = parsed.event) !== null && _a !== void 0 ? _a : "").toLowerCase();
                    const payload = parsed.data;
                    if (eventName && eventName !== "ping") {
                        return false;
                    }
                    if (payload !== undefined && this.hasSuspensionFlag(payload)) {
                        return true;
                    }
                    if ((payload === undefined || payload === null) &&
                        this.hasSuspensionFlag(parsed)) {
                        return true;
                    }
                }
            }
            catch (_b) {
                return false;
            }
        }
        if (data && typeof data === "object") {
            return this.hasSuspensionFlag(data);
        }
        return false;
    }
}
